// src/pages/dataInput/utils/fkSuggester.ts
// ─────────────────────────────────────────────────────────────────────────────
// Sample-based FK relationship suggester for CSVs and Excel sheets.
// Mirrors the logic in profiling_utils.py:detect_foreign_keys but uses just
// the first N rows of each source to keep the browser responsive.
//
// Confidence is value-overlap percentage between candidate child column and
// candidate parent PK column.
// ─────────────────────────────────────────────────────────────────────────────

import { readSheetSample } from "./sheetValidation";

export interface FKSuggestion {
  from_table:  string;
  from_column: string;
  to_table:    string;
  to_column:   string;
  confidence:  number;       // 0..100 — value overlap %
  reason:      string;       // "ID-like name + 87% value overlap" etc.
}

export interface SourceForFK {
  /** Display label — typically the file name or sheet name */
  label:    string;
  /** Source-prefix-style identifier used in payloads (uppercase, underscored) */
  prefix:   string;
  /** Already-loaded headers (uppercased for matching) */
  headers:  string[];
  /** Rows as 2-D array (samples only) */
  rows:     any[][];
}

const SAP_PK_WHITELIST = new Set([
  "MATNR", "KUNNR", "LIFNR", "BUKRS", "VKORG", "WERKS",
  "EBELN", "BELNR", "AUFNR", "OBJNR", "MANDT",
]);

const EXCLUDE_KEYWORDS = [
  "DATE", "DT", "TM", "TS", "STAMP", "TIME",
  "NAME", "DESC", "DESCRIPTION", "TITLE", "LABEL",
  "COMMENT", "NOTE", "REMARK",
  "STATUS", "STATE", "FLAG", "TYPE", "CATEGORY",
  "ADDR", "ADDRESS", "CITY", "ZIP", "POSTAL",
  "EMAIL", "PHONE", "MOBILE",
];

const isIdLike = (col: string): boolean => {
  const u = col.toUpperCase();
  return (
    u === "ID" || u.endsWith("_ID") ||
    u.endsWith("_KEY") || u.endsWith("_CODE") ||
    u.endsWith("_NR")  || u.endsWith("_CD")  ||
    u.includes("UUID") || SAP_PK_WHITELIST.has(u)
  );
};

const looksExcluded = (col: string): boolean => {
  const u = col.toUpperCase();
  return EXCLUDE_KEYWORDS.some(kw => u.includes(kw));
};

/** Best-effort PK detection on a sampled source (uniqueness + ID-like name). */
function detectPKsFromSample(src: SourceForFK): string[] {
  const pks: { col: string; score: number }[] = [];
  for (let i = 0; i < src.headers.length; i++) {
    const col = src.headers[i];
    if (!col || looksExcluded(col)) continue;

    const values = src.rows.map(r => String(r[i] ?? "").trim()).filter(v => v !== "");
    if (values.length === 0) continue;
    const unique = new Set(values);
    const isUnique = unique.size === values.length;
    if (!isUnique) continue;

    let score = 0;
    if (isIdLike(col))                  score += 5;
    if (/^\d+$/.test(values[0] || ""))  score += 2;  // numeric-looking
    if (score > 0) pks.push({ col, score });
  }
  pks.sort((a, b) => b.score - a.score);
  return pks.map(p => p.col);
}

function valueOverlap(child: any[], parent: any[]): number {
  if (child.length === 0) return 0;
  const childSet  = new Set(child.map(v => String(v ?? "").trim()).filter(v => v !== ""));
  const parentSet = new Set(parent.map(v => String(v ?? "").trim()).filter(v => v !== ""));
  if (childSet.size === 0) return 0;
  let hits = 0;
  childSet.forEach(v => { if (parentSet.has(v)) hits++; });
  return Math.round((hits / childSet.size) * 100);
}

/**
 * Build FK suggestions across N sampled sources.
 * minConfidence: ignore matches below this threshold (default 60%).
 */
export function suggestFKs(
  sources: SourceForFK[],
  opts: { minConfidence?: number } = {}
): FKSuggestion[] {
  const minConfidence = opts.minConfidence ?? 60;
  const suggestions: FKSuggestion[] = [];

  // Pre-detect candidate PKs in each source
  const pksBySource: Record<string, string[]> = {};
  for (const s of sources) pksBySource[s.prefix] = detectPKsFromSample(s);

  for (const child of sources) {
    for (let ci = 0; ci < child.headers.length; ci++) {
      const childCol = child.headers[ci];
      if (!childCol) continue;
      const childVals = child.rows.map(r => r[ci]).filter(v => v != null && String(v).trim() !== "");
      if (childVals.length === 0) continue;

      for (const parent of sources) {
        if (parent.prefix === child.prefix) continue;

        for (const pk of pksBySource[parent.prefix] || []) {
          const pi = parent.headers.indexOf(pk);
          if (pi < 0) continue;
          const parentVals = parent.rows.map(r => r[pi]).filter(v => v != null && String(v).trim() !== "");
          if (parentVals.length === 0) continue;

          const conf = valueOverlap(childVals, parentVals);
          if (conf < minConfidence) continue;

          const reason =
            isIdLike(childCol)
              ? `'${childCol}' looks like an ID and ${conf}% of values match '${parent.label}.${pk}'.`
              : `${conf}% of '${childCol}' values match '${parent.label}.${pk}'.`;

          suggestions.push({
            from_table:  child.prefix,
            from_column: childCol,
            to_table:    parent.prefix,
            to_column:   pk,
            confidence:  conf,
            reason,
          });
        }
      }
    }
  }

  // Sort highest confidence first; dedupe identical (from_t,from_c,to_t,to_c)
  const seen = new Set<string>();
  return suggestions
    .sort((a, b) => b.confidence - a.confidence)
    .filter(s => {
      const k = `${s.from_table}|${s.from_column}|${s.to_table}|${s.to_column}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

/**
 * Helper: build SourceForFK list from a list of File objects (CSV) and/or
 * pre-classified Excel sheet info. The wizard calls this after the user has
 * picked sheets to include.
 */
export interface FileForFK {
  file:       File;
  sheetName?: string | null;     // null/undefined = whole CSV
  prefix:     string;            // source-prefix style id
  label:      string;            // display label
}

export async function buildFKSourcesFromFiles(items: FileForFK[]): Promise<SourceForFK[]> {
  const out: SourceForFK[] = [];
  for (const it of items) {
    try {
      const { headers, rows } = await readSheetSample(
        it.file, it.sheetName ?? null, 200
      );
      if (headers.length === 0) continue;
      out.push({
        label:   it.label,
        prefix:  it.prefix,
        headers: headers.map(h => h.toUpperCase()),
        rows,
      });
    } catch (err) {
      console.warn(`[fkSuggester] Failed to sample ${it.label}:`, err);
    }
  }
  return out;
}